import _ from 'lodash';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import shallow from 'zustand/shallow';
import { Store } from '../../store';
import { useVerifiers } from './graphql';
import { useEventsStore } from './store';

interface Option {
  id: string;
  email: string;
  name: string;
}

const Root = styled.div`
  position: relative;
`;

const Control = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  width: 270px;
  min-height: 34px;
  background: rgba(17, 27, 51, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 6px 12px;
  cursor: text;

  .label {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    flex-shrink: 0;
    margin:0px;

    i {
      color: rgb(159, 176, 255);
      font-size: 12px;
    }

    .text {
      text-transform: uppercase;
      font-weight: 700;
      font-size: 9px;
      color: rgba(176, 186, 209, 0.6);
    }
  }

  input {
    flex: 1 1 0%;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    color: rgb(240, 242, 245);
    font-weight: 700;
    font-size: 13px;

    &::placeholder {
      color: rgba(213, 221, 238, 0.6);
      font-weight: 700;
    }
  }

  .caret {
    flex-shrink: 0;
    font-size: 13px;
    color: var(--white60);
    transition: transform 0.2s;
  }

  &.open .caret {
    transform: rotate(180deg);
  }
`;

const MenuWrap = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 40;
`;

const Menu = styled.div`
  min-width: 240px;
  max-height: 280px;
  overflow-y: auto;
  padding: 4px;
  border-radius: 8px;
  background: #707685;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
`;

const Item = styled.button<{ $active: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  background: ${({ $active }): string => ($active ? '#1967d2' : 'transparent')};
  color: rgb(255, 255, 255);
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover,
  &:focus {
    outline: none;
    background: ${({ $active }): string => ($active ? '#1967d2' : 'rgba(255, 255, 255, 0.14)')};
  }
`;

const Empty = styled.div`
  padding: 10px 12px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
`;

// Sits to the right of the menu (a sibling, so the menu's scroll can't clip it).
const Detail = styled.div`
  position: absolute;
  top: 0;
  left: 100%;
  margin-left: 8px;
  min-width: 160px;
  max-width: 240px;
  padding: 8px 10px;
  border-radius: 7px;
  background: #0a1326;
  border: 1px solid rgba(255, 255, 255, 0.14);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.4);

  .entry {
    font-size: 11px;
    font-weight: 600;
    color: #d5ddee;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .entry + .entry {
    margin-top: 4px;
  }

  .k {
    color: rgba(176, 186, 209, 0.6);
    margin-right: 5px;
  }
`;

const displayName = (user: { first_name?: string | null; last_name?: string | null }): string =>
  _.compact([user.first_name, user.last_name]).join(' ');

export const ValidatorFilter = (): ReactElement => {
  const { me } = Store.User.useIdentity();
  const verifiers = useVerifiers();

  const { verifierEmail, setVerifierEmail } = useEventsStore((v) => ({
    verifierEmail: v.filters.verifierEmail,
    setVerifierEmail: v.setVerifierEmail
  }), shallow);

  const currentEmail = me?.email;
  const selectedEmail = verifierEmail ?? currentEmail;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hovered, setHovered] = useState<string>();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close and reset the query when clicking outside or pressing Escape.
  useEffect(() => {
    const onDown = (event: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);

    return (): void => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // Current user first (labeled "You"), then the rest, deduped by email.
  const options = useMemo<Option[]>(() => {
    const list: Option[] = [];
    const seen = new Set<string>();

    const push = (id?: string, email?: string | null, rawName?: string): void => {
      if (email && !seen.has(email)) {
        seen.add(email);

        const base = rawName !== undefined && rawName.length > 0 ? rawName : email;
        const name = `${base}${email === currentEmail ? ' (You)' : ''}`;

        list.push({ id: id ?? '', email, name });
      }
    };

    if (me) {
      push(String(me.id), me.email, displayName(me));
    }

    for (const verifier of verifiers) {
      push(verifier.id, verifier.email, displayName(verifier));
    }

    return list;
  }, [verifiers, me, currentEmail]);

  const filtered = useMemo<Option[]>(() => {
    const q = query.trim().toLowerCase();

    if (q.length === 0) {
      return options;
    }

    return options.filter(
      (o) => o.name.toLowerCase().includes(q) || o.email.toLowerCase().includes(q) || o.id.includes(q)
    );
  }, [options, query]);

  const selected = options.find((o) => o.email === selectedEmail);
  const detail = hovered !== undefined ? options.find((o) => o.email === hovered) : undefined;

  return (
    <Root ref={rootRef}>
      <Control
        className={open ? 'open' : ''}
        onClick={(): void => {
          setOpen(true);
          inputRef.current?.focus();
        }}
      >
        <span className='label'>
          <i className='fas fa-check' />
          <span className='text'>Validator</span>
        </span>

        <input
          ref={inputRef}
          value={open ? query : selected?.name ?? ''}
          placeholder={selected?.name ?? 'Select validator'}
          onFocus={(): void => {
            setOpen(true);
          }}
          onChange={(event): void => {
            setQuery(event.target.value);
            setOpen(true);
          }}
        />

        <i className='caret fas fa-angle-down' />
      </Control>

      {open && (
        <MenuWrap>
          <Menu
            className='cc-scroll'
            onMouseLeave={(): void => {
              setHovered(undefined);
            }}
          >
            {filtered.length === 0 ? (
              <Empty>No matches</Empty>
            ) : (
              filtered.map((option) => (
                <Item
                  key={option.email}
                  type='button'
                  $active={option.email === selectedEmail}
                  onMouseEnter={(): void => {
                    setHovered(option.email);
                  }}
                  onClick={(): void => {
                    setVerifierEmail(option.email);
                    setOpen(false);
                    setQuery('');
                  }}
                >
                  {option.name}
                </Item>
              ))
            )}
          </Menu>

          {detail && (
            <Detail>
              <div className='entry'>
                <span className='k'>ID</span>
                {detail.id || '—'}
              </div>
              <div className='entry'>
                <span className='k'>Email</span>
                {detail.email}
              </div>
            </Detail>
          )}
        </MenuWrap>
      )}
    </Root>
  );
};
